import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CreditCard, Check, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function MembershipPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId, plan, memberType } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPayment, setCurrentPayment] = useState('registration'); // 'registration' or 'annual'
  const [paymentStatus, setPaymentStatus] = useState({
    registration: 'pending',
    annual: 'pending'
  });

  // Pricing configuration
  const pricing = {
    registration: {
      individual: 10000,
      institutional: plan?.tier_level === 1 ? 25000 : plan?.tier_level === 2 ? 50000 : 75000
    },
    annual: {
      individual: 3000,
      institutional: plan?.tier_level === 1 ? 5000 : plan?.tier_level === 2 ? 10000 : 15000
    }
  };

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    // Fetch payment status
    if (applicationId) {
      fetchPaymentStatus();
    }

    return () => {
      document.body.removeChild(script);
    };
  }, [applicationId]);

  const fetchPaymentStatus = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/membership-payments/status/${applicationId}`);
      setPaymentStatus({
        registration: data.registration_payment_status || 'pending',
        annual: data.annual_payment_status || 'pending'
      });

      // If registration is complete, show annual payment
      if (data.registration_payment_status === 'completed') {
        setCurrentPayment('annual');
      }
    } catch (err) {
      console.error('Failed to fetch payment status:', err);
    }
  };

  const handlePayment = async (paymentType) => {
    if (!applicationId) {
      setError('Application ID not found. Please restart the registration process.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create Razorpay order
      const { data: orderData } = await axios.post(
        `${API_BASE_URL}/api/membership-payments/create-order`,
        null,
        {
          params: {
            application_id: applicationId,
            payment_type: paymentType,
            member_type: memberType,
            tier_level: plan?.tier_level || null
          }
        }
      );

      const amount = memberType === 'individual' 
        ? pricing[paymentType].individual 
        : pricing[paymentType].institutional;

      // Initialize Razorpay
      if (!window.Razorpay) {
        setError('Payment gateway failed to load. Please refresh the page.');
        setLoading(false);
        return;
      }

      if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
        setError('Payment gateway is not configured. Please contact support.');
        console.error('REACT_APP_RAZORPAY_KEY_ID is not set');
        setLoading(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: 'INR',
        order_id: orderData.id,
        name: 'CSED Membership',
        description: paymentType === 'registration' 
          ? 'Membership Registration Fee' 
          : 'Annual Membership Fee',
        image: '/logo.png',
        handler: async function (response) {
          // Verify payment
          try {
            await axios.post(
              `${API_BASE_URL}/api/membership-payments/verify-payment`,
              null,
              {
                params: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature
                }
              }
            );

            // Update status
            setPaymentStatus(prev => ({
              ...prev,
              [paymentType]: 'completed'
            }));

            // If registration complete, move to annual payment
            if (paymentType === 'registration') {
              setCurrentPayment('annual');
            } else {
              // Both payments complete - redirect to success page
              navigate('/membership/payment-success', {
                state: { applicationId, memberType }
              });
            }

            setLoading(false);
          } catch (err) {
            setError('Payment verification failed. Please contact support.');
            setLoading(false);
          }
        },
        prefill: {
          email: '',
          contact: ''
        },
        theme: {
          color: '#E87722'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  if (!applicationId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Invalid Access</h1>
          <p className="text-gray-600 mb-6">Application ID not found.</p>
          <Button onClick={() => navigate('/membership')}>Return to Membership Plans</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const registrationAmount = memberType === 'individual' 
    ? pricing.registration.individual 
    : pricing.registration.institutional;
  
  const annualAmount = memberType === 'individual' 
    ? pricing.annual.individual 
    : pricing.annual.institutional;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#1E3A5F]/90 text-white py-16 pt-32">
        <div className="container mx-auto px-4 text-center">
          <CreditCard className="w-16 h-16 text-[#E87722] mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">Complete Your Membership Payment</h1>
          <p className="text-gray-200 max-w-2xl mx-auto">
            Two-step payment process: Registration fee first, then Annual membership fee
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {error && (
          <div className="max-w-3xl mx-auto mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <div className="max-w-3xl mx-auto space-y-6">
          {/* Step 1: Registration Fee */}
          <Card className={`border-2 ${paymentStatus.registration === 'completed' ? 'border-green-500 bg-green-50' : currentPayment === 'registration' ? 'border-[#E87722]' : 'border-gray-200'}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${paymentStatus.registration === 'completed' ? 'bg-green-500' : 'bg-[#E87722]'}`}>
                    {paymentStatus.registration === 'completed' ? <Check className="w-5 h-5" /> : '1'}
                  </span>
                  Registration Fee
                </span>
                <span className="text-2xl font-bold text-[#E87722]">
                  ₹{registrationAmount.toLocaleString('en-IN')}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                One-time registration fee for {memberType === 'individual' ? 'Individual Professional' : `Institutional Member (Tier ${plan?.tier_level || 1})`}
              </p>
              {paymentStatus.registration === 'completed' ? (
                <div className="flex items-center gap-2 text-green-600 font-semibold">
                  <Check className="w-5 h-5" />
                  Payment Completed
                </div>
              ) : (
                <Button
                  onClick={() => handlePayment('registration')}
                  disabled={loading}
                  className="w-full bg-[#E87722] hover:bg-[#d66a1a]"
                >
                  {loading && currentPayment === 'registration' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Pay Registration Fee'
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Annual Fee */}
          <Card className={`border-2 ${paymentStatus.annual === 'completed' ? 'border-green-500 bg-green-50' : currentPayment === 'annual' && paymentStatus.registration === 'completed' ? 'border-[#E87722]' : 'border-gray-200 opacity-60'}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${paymentStatus.annual === 'completed' ? 'bg-green-500' : 'bg-gray-400'}`}>
                    {paymentStatus.annual === 'completed' ? <Check className="w-5 h-5" /> : '2'}
                  </span>
                  Annual Membership Fee
                </span>
                <span className="text-2xl font-bold text-[#E87722]">
                  ₹{annualAmount.toLocaleString('en-IN')}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                Annual membership fee (renewable yearly)
              </p>
              {paymentStatus.annual === 'completed' ? (
                <div className="flex items-center gap-2 text-green-600 font-semibold">
                  <Check className="w-5 h-5" />
                  Payment Completed
                </div>
              ) : paymentStatus.registration === 'completed' ? (
                <Button
                  onClick={() => handlePayment('annual')}
                  disabled={loading}
                  className="w-full bg-[#E87722] hover:bg-[#d66a1a]"
                >
                  {loading && currentPayment === 'annual' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Pay Annual Fee'
                  )}
                </Button>
              ) : (
                <p className="text-gray-500 italic">Complete registration payment first</p>
              )}
            </CardContent>
          </Card>

          {/* Total Summary */}
          <Card className="bg-blue-50 border-2 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700">Registration Fee:</span>
                <span className="font-semibold">₹{registrationAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700">Annual Fee:</span>
                <span className="font-semibold">₹{annualAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="border-t-2 border-blue-300 my-3"></div>
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-[#1E3A5F]">Total Amount:</span>
                <span className="text-2xl font-bold text-[#E87722]">
                  ₹{(registrationAmount + annualAmount).toLocaleString('en-IN')}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
}
